/* eslint-disable */
import { get, shuffle } from 'lodash';
import moment from 'moment';
import { markRaw } from 'vue';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import accountLogin from '@/components/cms/account-combined-login/account-combined-login.vue';
import { CMS_MARKET_RUNTIME, CmsMarketContractListDisplayType } from '@/components/cms/cms-config';
import { resolveDisplayType } from '@/components/cms/cms-market-list-resolver';
import { createCmsMarketRuntime } from '@/components/cms/cms-market-runtime';
import documentTableView from '@/components/cms/document-table-view/document-table-view.vue';
import documentTileView from '@/components/cms/document-tile-view/document-tile-view.vue';
import h2hMarketTileSet from '@/components/cms/h2h/h2h-market-tile-set/h2h-market-tile-set.vue';
import { resolveHeadToHeadSettings } from '@/components/cms/h2h/h2h-model';
import mapWidgetView from '@/components/cms/map-widget-view/map-widget-view.vue';
import { resolveColorListSettings } from '@/components/cms/market-contract-list-colorlist/color-list-model';
import marketContractListColorList from '@/components/cms/market-contract-list-colorlist/market-contract-list-colorlist.vue';
import marketContractListEmbedCompact from '@/components/cms/market-contract-list-v2-embed-compact/market-contract-list-v2-embed-compact.vue';
import marketContractListEmbed from '@/components/cms/market-contract-list-v2-embed/market-contract-list-v2-embed.vue';
import marketListItemEmbed from '@/components/cms/market-list-item-v2-embed/market-list-item-v2-embed.vue';
import videoView from '@/components/cms/video-view/video-view.vue';
import collapsiblePanel from '@/components/collapsible-panel/collapsible-panel.vue';
import linkComponent from '@/components/generics/link/link.vue';
import { PODCAST } from '@/components/home/home-old/podcasts/podcasts';
import podcastsComponent from '@/components/home/home-old/podcasts/podcasts.vue';
import marketCharts from '@/components/market/market-detail-charts/market-detail-charts.vue';
import { ONBOARDING_STEPS } from '@/components/onboarding/consts';
import twitter from '@/components/twitter/twitter';
import { pushStoryInteraction, INTERACTION_TYPE } from '@/helpers/dataLayer';
import log from '@/logger';
import accountVerifyMixin from '@/mixins/accountVerify';
import loaderMixin from '@/mixins/loader';
import { isWrapperApp } from '@/mixins/userAgent';
import store from '@/store';

let previewCarouselComponentPromise = null;
const getPreviewCarouselEmbedComponent = () => {
  if (!previewCarouselComponentPromise) {
    previewCarouselComponentPromise =
      import('@/components/cms/preview-carousel-embed/preview-carousel-embed.vue').then(
        (module) => module.default || module,
      );
  }
  return previewCarouselComponentPromise;
};

const dateGoDisplay = (dt) => {
  const daysDiff = moment().diff(dt, 'days');
  const hoursDiff = moment().diff(dt, 'hours');
  if (daysDiff > 365) {
    const years = Math.floor(daysDiff / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  } else if (daysDiff > 30) {
    const months = Math.floor(daysDiff / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  } else if (hoursDiff > 24) {
    return daysDiff === 1 ? '1 day ago' : `${daysDiff} days ago`;
  } else if (hoursDiff >= 1) {
    return hoursDiff === 1 ? '1 hour ago' : `${Math.floor(hoursDiff)} hours ago`;
  }
  return 'Within the last hour';
};

export default {
  mixins: [accountVerifyMixin, loaderMixin],
  inject: { inheritedCmsRuntime: { from: CMS_MARKET_RUNTIME, default: null } },
  data() {
    return {
      content: '',
      marketTileMarkets: [],
      contractListMarkets: [],
      marketContractListColorListObjects: [],
      marketContractListColorListData: [],
      cmsAccordionContentObjects: [],
      podcastItemContentObjects: [],
      chartWidgetObjects: [],
      docsWidgetObjects: [],
      videoWidgetObjects: [],
      mapWidgetObjects: [],
      cmsChartMarketCache: new Map(),
      headToHeadMarketTileSetObjects: [],
      headToHeadChartData: [],
      cmsWidgetApps: [],
      cmsScopedRuntimes: new Map(),
      cmsInstallGeneration: 0,
      cmsRuntime: null,
      cmsMarketRequests: 0,
    };
  },
  beforeUnmount() {
    this.cmsInstallGeneration += 1;
    this.destroyCmsWidgets();
    this.destroyMapWidgets();
    if (!this.inheritedCmsRuntime) this.cmsRuntime?.dispose();
  },
  methods: {
    getCmsRuntime() {
      if (!this.cmsRuntime) {
        this.cmsRuntime = markRaw(
          this.inheritedCmsRuntime ||
            createCmsMarketRuntime({
              loadMarket: this.loadCmsMarket,
              globals: { $gtm: this.$gtm },
            }),
        );
      }
      return this.cmsRuntime;
    },
    async loadCmsMarket(marketId, { refresh = false } = {}) {
      if (this.cmsMarketRequests++ === 0) this.loaderAdd(document.body);
      try {
        return await store.dispatch('market/getMarket', {
          marketId,
          modifyState: false,
          skipCache: refresh,
        });
      } finally {
        if (--this.cmsMarketRequests === 0) this.loaderRemove(document.body);
      }
    },
    getCmsContextForElement(el) {
      const scopeIndex = el.closest('[data-cms-story-index]')?.getAttribute('data-cms-story-index');
      const cms =
        scopeIndex != null && this.getStoryContentForLayout && this.storyMap?.[scopeIndex]
          ? this.getStoryContentForLayout(this.storyMap[scopeIndex]).cmsContent || {}
          : this;
      return { cms, scopeKey: scopeIndex == null ? 'surface' : `story:${scopeIndex}` };
    },
    getScopedCmsRuntime(scopeKey) {
      if (!this.cmsScopedRuntimes.has(scopeKey))
        this.cmsScopedRuntimes.set(scopeKey, markRaw(this.getCmsRuntime().createScope()));
      return this.cmsScopedRuntimes.get(scopeKey);
    },
    mountCmsWidget(component, props, el, runtime = this.getCmsRuntime()) {
      // Raw CMS HTML needs a separate Vue root with the shared app context.
      const app = runtime.createApp(component, props);
      this.cmsWidgetApps.push(markRaw(app));
      return app.mount(el);
    },
    destroyCmsWidgets() {
      this.cmsWidgetApps.forEach((app) => app.unmount());
      this.cmsWidgetApps = [];
      this.cmsScopedRuntimes.forEach((runtime) => runtime.dispose());
      this.cmsScopedRuntimes.clear();
    },
    installHeadToHeadWidgets() {
      for (const el of this.$el.querySelectorAll('.predictit__headtoheadmarkettileset')) {
        const { cms, scopeKey } = this.getCmsContextForElement(el);
        const list = cms.headToHeadMarketTileSetObjects || [];
        const settings = resolveHeadToHeadSettings(
          list,
          el.getAttribute('data-headtoheaduuid'),
          Number(el.getAttribute('data-headtoheadindex')),
        );
        if (settings) {
          const runtime = this.getScopedCmsRuntime(scopeKey);
          this.mountCmsWidget(
            h2hMarketTileSet,
            {
              settings,
              markets: cms.marketTileMarkets || [],
              chartData: cms.headToHeadChartData || [],
            },
            el,
            runtime,
          );
        }
      }
    },
    getTrackingContextForElement(el) {
      const regionEl = el.closest('[data-analytics-id]');
      const analyticsIdRaw = (regionEl && regionEl.getAttribute('data-analytics-id')) || '';
      const [storyId, storyVersionId, sectionId, regionId, regionTrackingId, ...rest] =
        analyticsIdRaw.split(/[-]/);
      return {
        storyId,
        storyVersionId: storyVersionId || 0,
        sectionId,
        regionId,
        regionTrackingId,
        previewUniqueViewGUID: this.previewUniqueViewGUID,
      };
    },
    installCarousels() {
      if (!this.$el.querySelectorAll('.predictit__carousel').length) return;
      const generation = this.cmsInstallGeneration;
      getPreviewCarouselEmbedComponent().then((PreviewCarouselComponent) => {
        if (generation !== this.cmsInstallGeneration) return;
        const carousels = this.$el.querySelectorAll('.predictit__carousel');
        for (const el of carousels) {
          const indicesString = el.getAttribute('data-carousel_indices');
          const indexes = indicesString ? indicesString.split(',').map((i) => i.trim()) : [];
          const trackingContext = this.getTrackingContextForElement(el);

          this.mountCmsWidget(
            PreviewCarouselComponent,
            {
              indexes: indexes,
              trackingContext,
            },
            el,
          );
        }
      });
    },
    installAccordions() {
      const accordionHeaders = this.$el.querySelectorAll('.predictit__accordion');
      for (const el of accordionHeaders) {
        const index = parseInt(el.getAttribute('data-accordionindex'), 10);
        const uuid = el.getAttribute('data-accordionuuid');
        const title = el.getAttribute('data-accordiontitle');
        const accordionObj = this.cmsAccordionContentObjects.find((i) => {
          if (uuid) {
            return i.regionUUID === uuid;
          } else {
            return i.accordionIndex === index;
          }
        });

        if (accordionObj) {
          const content = accordionObj.content || '';
          this.mountCmsWidget(
            collapsiblePanel,
            {
              title,
              content,
              isOpen: accordionObj.isOpenByDefault,
            },
            el,
          );
        }
      }
    },
    installTweets() {
      if (isWrapperApp) return;
      if (!this.$el.querySelector('.predictit__twitter')) return;
      twitter().ready((twt) => {
        const twitterWidgets = this.$el.querySelectorAll('.predictit__twitter');
        for (const widget of twitterWidgets) {
          const tweetId = widget.getAttribute('data-tweetid');
          twt.widgets.createTweet(tweetId, widget, {
            theme: 'light',
          });
        }
      });
    },
    installDateElapsed() {
      const dateEls = this.$el.querySelectorAll('.predictit__elapsedtime');
      for (const el of dateEls) {
        const date = moment(el.getAttribute('data-publishdate'));
        el.innerHTML = dateGoDisplay(date);
      }
    },
    installWelcomePopupControls() {
      const buttons = this.$el.querySelectorAll('.predictit-leaving-button');
      for (const button of buttons) {
        button.addEventListener('click', () => {
          store.dispatch('popup/setCurrentPopup', MODALS.LEAVING_POPUP);
        });
      }
    },
    installLoginAndSignUpControls() {
      const loginButtons = this.$el.querySelectorAll('.predictit-login-button');
      for (const button of loginButtons) {
        button.addEventListener('click', () => {
          if (store.getters['user/isLoggedIn']) {
            this.$router.push({ name: 'market-list' });
          } else {
            store.dispatch('popup/setCurrentPopup', MODALS.LOGIN);
          }
        });
      }
      const loginControls = this.$el.querySelectorAll('.predictit-login-control');
      for (const button of loginControls) {
        if (store.getters['user/isLoggedIn']) {
          this.$router.push({ name: 'market-list' });
        } else {
          this.mountCmsWidget(accountLogin, {}, button);
        }
      }

      const signUpButtons = this.$el.querySelectorAll('.predictit-signup-button');
      for (const button of signUpButtons) {
        button.addEventListener('click', () => {
          if (store.getters['user/isLoggedIn']) {
            this.$router.push({ name: 'market-list' });
          } else {
            store.dispatch('popup/setCurrentPopup', MODALS.REGISTER);
          }
        });
      }
    },
    installMarketLists() {
      const marketListEls = this.$el.querySelectorAll('.predictit__markettilelist');

      for (const el of marketListEls) {
        const marketIds = el
          .getAttribute('data-marketids')
          .replace(' ', '')
          .split(',')
          .map((i) => parseInt(i, 10));
        const markets = marketIds
          .map((marketId) => this.marketTileMarkets.find((i) => i.marketId === marketId))
          .filter((i) => !!i);
        const dataNumColumns = parseInt(el.getAttribute('data-num-columns') || 3, 10);
        const renderStyle = parseInt(el.getAttribute('data-render-style') || 1, 10);

        if (markets.length > 0) {
          const trackingContext = this.getTrackingContextForElement(el);
          this.mountCmsWidget(
            marketListItemEmbed,
            {
              markets,
              dataNumColumnsDesktop: dataNumColumns,
              renderStyle,
              trackingContext,
            },
            el,
          );
        }
      }
    },
    installMarketContracts() {
      for (const el of this.$el.querySelectorAll('.predictit__marketpreview')) {
        const { cms, scopeKey } = this.getCmsContextForElement(el);
        const marketId = Number(el.getAttribute('data-marketid'));
        const market = (cms.contractListMarkets || []).find((item) => item.marketId === marketId);
        if (!market) continue;
        const compact = (el.getAttribute('data-compactview') || '').toLowerCase() === 'true';
        const displayType = resolveDisplayType(el.getAttribute('data-displaytype'), compact);
        const openNewTab = (el.getAttribute('data-opennewtab') || '').toLowerCase() === 'true';
        const trackingContext = this.getTrackingContextForElement(el);
        const runtime = this.getScopedCmsRuntime(scopeKey);
        const contractIds = (el.getAttribute('data-contractids') || '').split(',').map(Number);
        const contracts = (market.contracts || []).filter((item) =>
          contractIds.includes(item.contractId),
        );
        switch (displayType) {
          case CmsMarketContractListDisplayType.ColorList: {
            const index = el.getAttribute('data-colorlistindex');
            const settings = resolveColorListSettings(
              cms.marketContractListColorListObjects,
              el.getAttribute('data-colorlistuuid'),
              index === null ? null : Number(index),
            );
            if (!settings || Number(settings.marketId) !== marketId) continue;
            const historyData =
              (cms.marketContractListColorListData || []).find(
                (item) => item.regionUUID === settings.regionUUID,
              ) || null;
            this.mountCmsWidget(
              marketContractListColorList,
              { settings, historyData, market, openNewTab, trackingContext },
              el,
              runtime,
            );
            break;
          }
          case CmsMarketContractListDisplayType.Compact:
            this.mountCmsWidget(
              marketContractListEmbedCompact,
              {
                openNewTab,
                trackingContext,
                contracts: contracts.map((item) => ({
                  ...item,
                  marketName: market.marketName,
                  marketId: market.marketId,
                })),
              },
              el,
              runtime,
            );
            break;
          case CmsMarketContractListDisplayType.Classic:
            this.mountCmsWidget(
              marketContractListEmbed,
              {
                openNewTab,
                trackingContext,
                market: { ...market, contracts: [...contracts] },
              },
              el,
              runtime,
            );
            break;
        }
      }
    },
    installViewMarketTracking(el, url) {
      const MARKET_URL_PATTERN = /^\/markets\//;
      if (!MARKET_URL_PATTERN.test(url || '')) return;
      const trackingContext = this.getTrackingContextForElement(el);
      el.addEventListener('click', () => {
        pushStoryInteraction({ interactionType: INTERACTION_TYPE.VIEW_MARKET, ...trackingContext });
      });
    },
    installButtonsAndLinks() {
      const links = this.$el.querySelectorAll('[data-buttontype="Link"]');
      for (const el of links) {
        const url = el.getAttribute('data-detailpath');
        const isStoryFooterCta =
          (el.getAttribute('data-story-footer-cta') || '').toLowerCase() === 'true';
        this.mountCmsWidget(
          linkComponent,
          {
            type: 'link',
            text: el.innerHTML,
            url,
            styleVariant: isStoryFooterCta ? 'story-footer-cta' : '',
          },
          el,
        );
        this.installViewMarketTracking(el, url);
      }

      const buttons = this.$el.querySelectorAll('[data-buttontype="Button"]');
      for (const el of buttons) {
        const url = el.getAttribute('data-detailpath');
        this.mountCmsWidget(
          linkComponent,
          {
            type: 'button',
            text: el.innerHTML,
            url,
          },
          el,
        );
        this.installViewMarketTracking(el, url);
      }
    },
    installPodcasts() {
      const podcastSliders = this.$el.querySelectorAll('.predictit__podcastslider');
      for (const el of podcastSliders) {
        const podcastUuids = (el.getAttribute('data-podcastuuids') || '')
          .split(',')
          .map((i) => i.trim())
          .filter((i) => i.length > 0);
        const podcasts = podcastUuids
          .map((index) =>
            this.podcastItemContentObjects.find((podcast) => podcast.regionUUID === index),
          )
          .filter((i) => !!i);
        const title = el.getAttribute('data-podcasttitle');
        const isRandomized =
          (el.getAttribute('data-podcastisrandomized') || '').toLowerCase() === 'true';

        let items = podcasts
          .map((i) => {
            const chunks = (i.podcastUrl || '')
              .split('/')
              .filter((chunk) => chunk && chunk.length > 0);
            return {
              id: chunks[chunks.length - 1] || '',
              type: PODCAST.APPLE,
            };
          })
          .filter((i) => i.id.length > 0);

        if (isRandomized) {
          items = shuffle(items);
        }
        const forceLayoutMode =
          typeof this.getCmsForcedLayoutMode === 'function' ? this.getCmsForcedLayoutMode() : '';
        this.mountCmsWidget(
          podcastsComponent,
          {
            theme: 'story',
            title,
            items,
            forceLayoutMode,
          },
          el,
        );
      }
    },
    resolveChartMarket(marketId) {
      const normalizedMarketId = parseInt(marketId, 10);
      if (isNaN(normalizedMarketId)) {
        return Promise.reject(new Error(`Invalid market id: ${marketId}`));
      }

      if (this.cmsChartMarketCache.has(normalizedMarketId)) {
        return this.cmsChartMarketCache.get(normalizedMarketId);
      }

      const request = store
        .dispatch('market/getMarket', { marketId: normalizedMarketId, modifyState: false })
        .then((market) => {
          if (!market) {
            throw new Error(`Market data not found for marketId=${normalizedMarketId}`);
          }
          return market;
        })
        .catch((error) => {
          this.cmsChartMarketCache.delete(normalizedMarketId);
          throw error;
        });

      this.cmsChartMarketCache.set(normalizedMarketId, request);
      return request;
    },
    installMarketCharts(rootEl = null, { suppressGlobalLoader = false } = {}) {
      const generation = this.cmsInstallGeneration;
      const root = rootEl || this.$el;
      if (!root) {
        return;
      }
      const marketChartsEl = root.querySelectorAll('.predictit__datachart');
      for (const el of marketChartsEl) {
        const mountState = el.getAttribute('data-cms-chart-mounted');
        if (mountState === 'pending' || mountState === 'true') {
          continue;
        }

        const uuid = el.getAttribute('data-chartuuid');
        const versionId = el.getAttribute('data-chartstoryversionid');
        const storyId = el.getAttribute('data-chartstoryid');
        const maxHeight = el.getAttribute('data-max-height');

        const chartObj = this.chartWidgetObjects.find((i) => i.regionUUID === uuid);
        if (typeof chartObj === 'undefined') {
          continue;
        }

        const trackingContext = this.getTrackingContextForElement(el);
        el.setAttribute('data-cms-chart-mounted', 'pending');
        this.resolveChartMarket(chartObj.marketId)
          .then((market) => {
            if (generation !== this.cmsInstallGeneration || !el.isConnected) return;
            let result = { ...market };
            if (
              chartObj.contractIds &&
              chartObj.contractIds.length > 0 &&
              result &&
              result.contracts
            ) {
              result = {
                ...result,
                contracts: result.contracts.filter((i) =>
                  chartObj.contractIds.includes(i.contractId),
                ),
              };
            }
            this.mountCmsWidget(
              marketCharts,
              {
                market: result,
                chartOptions: {
                  ...chartObj,
                  cmsStoryId: storyId,
                  cmsVersionId: versionId,
                  includeDownload: false,
                  maxHeight,
                  // showContractImagesOnLegend: true,
                  // showContractImagesOnChart: true,
                },
                trackingContext,
                suppressGlobalLoader,
              },
              el,
            );
            el.setAttribute('data-cms-chart-mounted', 'true');
          })
          .catch((error) => {
            log.error('Failed to mount CMS chart widget.', error);
            el.removeAttribute('data-cms-chart-mounted');
          });
      }
    },
    installVideoWidgets() {
      const videoEls = this.$el.querySelectorAll('.predictit__videowidget');
      for (const el of videoEls) {
        const url = el.getAttribute('data-videourl');
        const maxHeight = el.getAttribute('data-max-height');
        const videoData = this.videoWidgetObjects.find((i) => i.videoUrl === url);
        const trackingContext = this.getTrackingContextForElement(el);
        this.mountCmsWidget(
          videoView,
          {
            ...videoData,
            maxHeight,
            trackingContext,
          },
          el,
        );
      }
    },
    installDocumentsWidgets() {
      const documentsEls = this.$el.querySelectorAll('.predictit__docswidget');
      for (const el of documentsEls) {
        const uuid = el.getAttribute('data-docsuuid');
        const viewType = parseInt(el.getAttribute('data-docsviewtype'), 10);
        const documentData = this.docsWidgetObjects.find((i) => i.regionUUID === uuid);
        if (documentData) {
          const componentMap = {
            1: documentTileView,
            2: documentTableView,
          };
          const component = componentMap[viewType] || documentTileView;
          this.mountCmsWidget(component, { documentData }, el);
        }
      }
    },
    destroyMapWidgets() {
      // Each map widget is its own createApp() root, outside this component's render tree, so
      // Vue does not unmount it with the page; without this its chart and document listeners
      // outlive the page.
      (this.mapWidgetApps || []).forEach((widgetApp) => widgetApp.unmount());
      this.mapWidgetApps = [];
    },
    installMapWidgets() {
      this.destroyMapWidgets();
      const mapEls = this.$el.querySelectorAll('.predictit__mapwidget');
      for (const el of mapEls) {
        const uuid = el.getAttribute('data-mapwidgetuuid');
        const mapNameFromElement = el.getAttribute('data-mapname');
        const mapWidgetData = this.mapWidgetObjects.find((i) => i.regionUUID === uuid);
        const mapName = get(mapWidgetData, 'mapName', '') || mapNameFromElement;
        if (!mapName) {
          continue;
        }

        const widgetApp = this.getCmsRuntime().createApp(mapWidgetView, { mapName });
        widgetApp.mount(el);
        this.mapWidgetApps.push(widgetApp);
      }
    },
    installDynamicControls({
      data = {},
      marketChartsRootEl = null,
      suppressMarketChartsGlobalLoader = false,
      skipMarketCharts = false,
      skipCarousel = false,
    } = {}) {
      this.destroyCmsWidgets();
      const generation = ++this.cmsInstallGeneration;
      this.css = get(data, 'renderedCSS', '') || '';
      this.content = `${this.css}${get(data, 'renderedHTML', '')}`;
      this.cmsAccordionContentObjects = get(data, 'cmsContentObject.accordionContentObjects', []);
      this.podcastItemContentObjects = get(data, 'cmsContentObject.podcastItemContentObjects', []);
      this.contractListMarkets = get(data, 'cmsContentObject.contractListMarkets', []) || [];
      this.marketContractListColorListObjects =
        get(data, 'cmsContentObject.marketContractListColorListObjects', []) || [];
      this.marketContractListColorListData =
        get(data, 'cmsContentObject.marketContractListColorListData', []) || [];
      this.marketTileMarkets = get(data, 'cmsContentObject.marketTileMarkets', []);
      this.chartWidgetObjects = get(data, 'cmsContentObject.chartWidgetObjects', []);
      this.docsWidgetObjects = get(data, 'cmsContentObject.docsWidgetObjects', []);
      this.videoWidgetObjects = get(data, 'cmsContentObject.videoWidgetObjects', []);
      this.mapWidgetObjects = get(data, 'cmsContentObject.mapWidgetObjects', []);
      this.headToHeadMarketTileSetObjects =
        get(data, 'cmsContentObject.headToHeadMarketTileSetObjects', []) || [];
      this.headToHeadChartData = get(data, 'cmsContentObject.headToHeadChartData', []) || [];

      this.$nextTick(() => {
        if (generation !== this.cmsInstallGeneration) return;
        this.installAccordions();
        this.installTweets();
        this.installDateElapsed();
        this.installLoginAndSignUpControls();
        this.installWelcomePopupControls();
        this.installMarketLists();
        this.installMarketContracts();
        this.installHeadToHeadWidgets();
        this.installButtonsAndLinks();
        this.installPodcasts();
        if (!skipMarketCharts) {
          this.installMarketCharts(marketChartsRootEl, {
            suppressGlobalLoader: suppressMarketChartsGlobalLoader,
          });
        }
        this.installDocumentsWidgets();
        this.installVideoWidgets();
        if (!skipCarousel) {
          this.installCarousels();
        }
        this.installMapWidgets();
      });
    },
  },
};
